import type { Substance } from '../types';

export const morphine: Substance = {
  id: 'morphine',
  displayName: 'Morphine',
  emoji: '🌺',
  aliases: ['morphine', 'opiate'],
  substanceClass: 'opioid',
  mechanismSummary:
    'μ-opioid agonist. Acts at three levels at once: the spinal dorsal horn (blocking pain signals as they ' +
    'enter), the brain stem and limbic system (analgesia and euphoria), and the enteric nervous system — ' +
    'which is why constipation is near-universal and does not fade with tolerance. Respiratory depression, ' +
    'the cause of overdose death, comes from μ-receptors in the brain stem.',
  pk: {
    route: 'oral',
    ka: 1.3,
    ke: 0.231,
    vd: 230,
    bioavailability: 0.3,
    molecularWeightGMol: 285.34,
    brainPlasmaRatio: 0.3,
  },
  doseRange: { min: 5, max: 60, default: 15, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'MOR',
      kiNM: 1.2,
      action: 'agonist',
      citation:
        'Volpe DA et al. (2011) "Uniform assessment and ranking of opioid Mu receptor binding constants," ' +
        'Regul Toxicol Pharmacol 59:385-390 — places morphine in the 1-100 nM band.',
    },
    {
      receptor: 'KOR',
      kiNM: 47,
      action: 'partial-agonist',
      citation: 'Approximate; morphine is far more selective for mu. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'DOR',
      kiNM: 120,
      action: 'partial-agonist',
      citation: 'Approximate; weak delta affinity. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#cc7799', secondary: '#8855aa', particleSpeedMultiplier: 0.5 },
};

export const fentanyl: Substance = {
  id: 'fentanyl',
  displayName: 'Fentanyl',
  emoji: '⚠️',
  aliases: ['fentanyl'],
  substanceClass: 'opioid',
  mechanismSummary:
    'A synthetic μ-opioid agonist. Its binding affinity is broadly similar to morphine\'s — its far greater ' +
    'potency comes mostly from high lipophilicity driving very rapid brain entry, plus greater efficacy at ' +
    'the receptor. That speed of onset is precisely what makes the margin between dose and overdose so ' +
    'narrow: an important lesson that raw affinity numbers alone do not convey.',
  pk: {
    route: 'oral',
    ka: 3.0,
    ke: 0.1155,
    vd: 280,
    bioavailability: 0.33,
    molecularWeightGMol: 336.47,
    brainPlasmaRatio: 2.2,
  },
  doseRange: { min: 0.025, max: 0.4, default: 0.1, step: 0.025, unit: 'µg', displayScale: 1000 },
  bindings: [
    {
      receptor: 'MOR',
      kiNM: 1.35,
      action: 'agonist',
      citation:
        'Volpe DA et al. (2011) Regul Toxicol Pharmacol 59:385-390 — uniform assessment places fentanyl ' +
        'and morphine in the same 1-100 nM band; fentanyl\'s clinical potency is driven by kinetics and ' +
        'efficacy rather than by binding affinity alone.',
    },
  ],
  colorPalette: { primary: '#ff5544', secondary: '#aa2233', particleSpeedMultiplier: 0.4 },
};

export const codeine: Substance = {
  id: 'codeine',
  displayName: 'Codeine',
  emoji: '🍶',
  aliases: ['codeine'],
  substanceClass: 'opioid',
  mechanismSummary:
    'A weak μ-opioid agonist that is largely a prodrug: most of its effect comes from the fraction ' +
    'metabolised into morphine by the liver enzyme CYP2D6. Because CYP2D6 activity varies enormously ' +
    'between people, the same dose can do almost nothing in one person and be dangerous in another.',
  pk: {
    route: 'oral',
    ka: 1.5,
    ke: 0.231,
    vd: 260,
    bioavailability: 0.6,
    molecularWeightGMol: 299.36,
    brainPlasmaRatio: 0.4,
  },
  doseRange: { min: 15, max: 120, default: 30, step: 15, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'MOR',
      kiNM: 700,
      action: 'partial-agonist',
      citation:
        'Volpe DA et al. (2011) Regul Toxicol Pharmacol 59:385-390 places codeine in the >100 nM (weak) ' +
        'band. Note this model does not simulate CYP2D6 conversion to morphine, which carries most of the effect.',
    },
  ],
  colorPalette: { primary: '#bb88bb', secondary: '#886699', particleSpeedMultiplier: 0.6 },
};

export const naloxone: Substance = {
  id: 'naloxone',
  displayName: 'Naloxone',
  emoji: '🩺',
  aliases: ['naloxone', 'narcan'],
  substanceClass: 'opioid',
  mechanismSummary:
    'A μ-opioid ANTAGONIST — it occupies the receptor without activating it, and displaces agonists already ' +
    'bound. This is the overdose reversal agent: it restores breathing within minutes. Its half-life is ' +
    'shorter than many opioids, so overdose can return as it wears off.',
  pk: {
    route: 'oral',
    ka: 4.0,
    ke: 0.63,
    vd: 140,
    bioavailability: 0.9,
    molecularWeightGMol: 327.37,
    brainPlasmaRatio: 1.4,
  },
  doseRange: { min: 0.4, max: 4, default: 2, step: 0.4, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'MOR',
      kiNM: 3.1,
      action: 'antagonist',
      citation: 'Volpe DA et al. (2011) Regul Toxicol Pharmacol 59:385-390 — naloxone Ki 3.1 nM at mu.',
    },
    {
      receptor: 'KOR',
      kiNM: 16,
      action: 'antagonist',
      citation: 'Approximate; naloxone is a non-selective opioid antagonist. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#55ddaa', secondary: '#33aa88', particleSpeedMultiplier: 1.0 },
};

export const OPIOIDS = [morphine, fentanyl, codeine, naloxone];
